/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import clsx from 'clsx';
import { TaxesAndFeesIncludedLabel } from './TaxesAndFeesIncludedLabel';

type MonetaryBlock = {
  amount?: number;
  decimalPoint?: number;
  currency?: string;
};

type StrikeThroughVariant = 'taxes' | 'cash';

export const StrikeThroughPriceDisplay: React.FC<{
  variant: StrikeThroughVariant;
  displayBlock: MonetaryBlock;
  eeoBlock: MonetaryBlock;
  longPriceCheckBlock: MonetaryBlock;
  eeoAriaCurrency?: string;
  showStrikeThroughPrice: boolean;
  fromHotelCard?: boolean;
  isMapViewEnabled?: boolean;
  currentLocale?: string;
  currentViewIsMobile?: boolean;
  currentViewIsTab?: boolean;
  HQVRateBlockFlag?: boolean;
  HQVTaxesandFeeFlag?: boolean;
  selectedCurrency?: string;
  staticContent: any;
  formatPrice: (block?: MonetaryBlock | null) => string;
  perNightCurrencyLabel: (rateCurrency?: string) => string;
  isStrikethroughMandatoryFee: boolean;
  isDeMapListSection?: boolean;
  isViewportL?: boolean;
  includeTaxesAndFees?: boolean;
}> = ({
  variant,
  displayBlock,
  eeoBlock,
  longPriceCheckBlock,
  eeoAriaCurrency,
  showStrikeThroughPrice,
  fromHotelCard,
  isMapViewEnabled,
  currentLocale,
  currentViewIsMobile,
  currentViewIsTab,
  HQVRateBlockFlag,
  HQVTaxesandFeeFlag,
  selectedCurrency,
  staticContent,
  formatPrice,
  perNightCurrencyLabel,
  isStrikethroughMandatoryFee,
  isDeMapListSection,
  isViewportL,
  includeTaxesAndFees,
}) => {
  const isTaxesVariant = variant === 'taxes';
  const regularAmount = displayBlock?.amount;
  const eeoAmount = eeoBlock?.amount;
  const showStrike = showStrikeThroughPrice && eeoAmount && regularAmount && regularAmount > eeoAmount;
  const eeoFormattedLength = formatPrice(eeoBlock).split('').length;
  const longPriceFormattedLength = formatPrice(longPriceCheckBlock).split('').length;
  const longPriceThresholdMet = isTaxesVariant ? longPriceFormattedLength >= 8 : longPriceFormattedLength > 8;

  const priceContent = (
    <span
      className={clsx(
        'price-value',
        showStrikeThroughPrice &&
          fromHotelCard &&
          eeoAmount &&
          regularAmount &&
          regularAmount > eeoAmount &&
          eeoFormattedLength <= 4
          ? isTaxesVariant
            ? 'map-currency-value'
            : 'map-currency-value '
          : isMapViewEnabled && longPriceThresholdMet
          ? 'd-flex flex-column align-items-start'
          : 'currency-value amount-display',
          (currentViewIsMobile || currentViewIsTab) && HQVRateBlockFlag && 'ml-3',
        !isTaxesVariant &&
          currentLocale === 'ru-RU' &&
          !HQVRateBlockFlag &&
          (currentViewIsMobile || currentViewIsTab) &&
          (selectedCurrency?.toLocaleLowerCase() === 'krw' || selectedCurrency?.toLocaleLowerCase() === 'idr')
          ? 'ru-specific-price-section'
          : ''
      )}
    >
      <span
        className={clsx(
          'price-value',
          showStrikeThroughPrice &&
            fromHotelCard &&
            eeoAmount &&
            regularAmount &&
            regularAmount > eeoAmount &&
            eeoFormattedLength <= 4
            ? 'map-price-value'
            : ''
        )}
      >
        {showStrike && (
          <span className={clsx(isStrikethroughMandatoryFee ? 'striked-mandatory-fee' : '', 'strike-through')}>
            <div
              className={clsx(
                't-label-xs',
                'eeo-price-value',
                fromHotelCard && eeoFormattedLength <= 3 ? 'strike-through-component' : ''
              )}
            >
              {staticContent.previousEEORateLabel} {formatPrice(displayBlock)}
            </div>
          </span>
        )}
        <span
          className="m-price"
          role="button"
          aria-label={`
                          ${staticContent?.newEEORateLabel}
                          ${
                            showStrikeThroughPrice && eeoAmount
                              ? formatPrice(
                                  isTaxesVariant && eeoAriaCurrency
                                    ? { ...eeoBlock, currency: eeoAriaCurrency }
                                    : eeoBlock
                                )
                              : formatPrice(displayBlock)
                          }

                         `}
        >
          {showStrikeThroughPrice && eeoAmount && (
            <span role="button" aria-label={staticContent.newEEORateLabel}></span>
          )}
          {showStrikeThroughPrice && eeoAmount ? formatPrice(eeoBlock) : formatPrice(displayBlock)}
        </span>
      </span>
      <span
        className={clsx(
          isTaxesVariant && HQVTaxesandFeeFlag ? 'taxes-currency-label' : '',
          isTaxesVariant
            ? formatPrice(displayBlock).split('').length <= 4
              ? 'currency-label'
              : isDeMapListSection && isViewportL
              ? 'd-block'
              : ''
            : formatPrice(displayBlock).split('').length <= 4
            ? 'currency-label'
            : isMapViewEnabled && !currentViewIsMobile && !currentViewIsTab && longPriceThresholdMet
            ? 'idr-currency-label idr-currency-label-map'
            : 'idr-currency-label',

          isTaxesVariant ? 't-label-xs' : HQVRateBlockFlag ? `t-font-m` : `t-label-xs`
        )}
        tabIndex={isTaxesVariant ? 0 : undefined}
        role={isTaxesVariant ? 'region' : 'button'}
        aria-label={isTaxesVariant ? undefined : ` ${perNightCurrencyLabel(displayBlock?.currency)}`}
      >
        {isTaxesVariant
          ? ` ${perNightCurrencyLabel(displayBlock?.currency)}`
          : perNightCurrencyLabel(displayBlock?.currency)}
      </span>
    </span>
  );

  if (isTaxesVariant) {
    return (
      <div className={clsx('taxes-and-fees-section')}>
        <div className="price-section">{priceContent}</div>
        <TaxesAndFeesIncludedLabel
          show={Boolean(includeTaxesAndFees || HQVTaxesandFeeFlag)}
          currentViewIsTab={Boolean(currentViewIsTab)}
          fromHotelCard={fromHotelCard}
          label={staticContent?.taxesAndFeesIncluded}
          deMapClass={isDeMapListSection}
        />
      </div>
    );
  }

  return priceContent;
};
